import { useEffect, useRef, useState } from 'react';

import {
  useRentalManagerPropertyAddressDetailsWebApiHandler_details,
  useRentalManagerPropertyAddressVerificationWebApiHandler_verification,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { resolveUnitAlert, validateNumUnits } from '../utils/propertyValidationUtils';

import AddressConstants from '../constants/AddressConstants';
import PropertyConstants from '../constants/PropertyConstants';
import TimeConstants from '../constants/TimeConstants';

import { useAuth } from '../components/AuthProvider';

import { useAddressAutocomplete } from './queries/address';
import { useDebouncedValue } from './useDebouncedValue';
import { usePropertyLocationCheck } from './usePropertyLocationCheck';

import type { ReactNode } from 'react';
import type { ChangeEvent } from 'react';
import type { ComboboxOptionType } from '@zillow/constellation-10';
import type { AddressComponents } from '@zg-rentals/rental-manager-env';
import type { RentalManagerPropertyAddressDetailsDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { UnitAlertValue } from '../constants/AddPropertyConstants';
import type { ImageValue } from '../components/modals/AddPropertyFlowModal/components/PropertyImageUpload';

const {
  PROPERTY_CODES_BY_TYPE,
  PROPERTY_TYPES_BY_ENUM,
  PROPERTY_TYPES: { COMMUNITY, CONDO, MULTIPLEX },
} = PropertyConstants;

export type AddPropertyDetailsState = {
  address: AddressComponents;
  propertyType: string;
  numUnits: string;
  image: File | null;
  imagePreviewUrl: string | null;
  isImpreciseLocation: boolean;
  listingAlias: string;
};

type Params = {
  onContinue: (state: AddPropertyDetailsState) => void;
};

export const useAddPropertyDetailsForm = ({ onContinue }: Params) => {
  const { user } = useAuth?.() ?? {};

  const [address, setAddress] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [unit, setUnit] = useState('');
  const [numUnits, setNumUnits] = useState('');
  const [imageValue, setImageValue] = useState<ImageValue>({ image: null, imagePreviewUrl: null });
  const [addressError, setAddressError] = useState('');
  const [propertyTypeError, setPropertyTypeError] = useState('');
  const [numUnitsError, setNumUnitsError] = useState('');
  const [propertyAlreadyPostedWarningMessage, setPropertyAlreadyPostedWarningMessage] = useState<
    string | Array<ReactNode>
  >('');
  const [unitAlert, setUnitAlert] = useState<UnitAlertValue | null>(null);

  const addressInputRef = useRef<HTMLInputElement>(null);
  const prevVerifiedRef = useRef({ address: '', unit: '' });
  const lastVerifiedDataRef = useRef<RentalManagerPropertyAddressDetailsDto | null>(null);

  const [setPartialAddress, queryData] = useAddressAutocomplete({
    debounceMs: TimeConstants.DEBOUNCE_TIME_500_MS,
  });

  // Undebounced, every keystroke re-keys this query and greys out the property type select.
  const debouncedAddress = useDebouncedValue(address, TimeConstants.DEBOUNCE_TIME_500_MS);

  const { data: addressDetailsData, isFetching: isLoadingAddressDetails } =
    useRentalManagerPropertyAddressDetailsWebApiHandler_details(
      { fullAddress: debouncedAddress, unit },
      {
        enabled: !!debouncedAddress,
        meta: {
          tags: ['address-details'],
          context: {
            api: 'PropertyAddressDetailsWebApiHandler',
            details: 'Auto-populate property type, beds, and baths from address',
          },
        },
      },
    );

  useEffect(() => {
    if (addressDetailsData?.propertyType && !propertyType) {
      const detectedType = PROPERTY_TYPES_BY_ENUM[addressDetailsData.propertyType];
      if (detectedType) setPropertyType(detectedType);
    }
  }, [addressDetailsData, propertyType]);

  const { isCheckingRestrictions, checkLocationRestriction: checkRestrictions } = usePropertyLocationCheck({
    user: user ?? undefined,
    setPropertyAlreadyPostedWarningMessage,
  });

  const checkLocationRestriction = async (currentAddress: string, currentPropertyType: string) => {
    const result = await checkRestrictions({ address: currentAddress, unit, propertyType: currentPropertyType });
    if (result?.errorMessage) {
      setPropertyAlreadyPostedWarningMessage(result.errorMessage);
    }
    if (result?.paidMultifamilyLocation && !currentPropertyType) {
      setPropertyType(unit ? CONDO : COMMUNITY);
    }
  };

  const propertyTypeCode = (PROPERTY_CODES_BY_TYPE as Record<string, number>)[propertyType];

  const { refetch: verifyAddress, isFetching: isVerifyingAddress } =
    useRentalManagerPropertyAddressVerificationWebApiHandler_verification(
      { fullAddress: address, unit, propertyTypeCode },
      {
        enabled: false,
        meta: {
          tags: ['address-verification'],
          context: { api: 'PropertyAddressVerificationWebApiHandler', details: 'Verify address on continue' },
        },
      },
    );

  const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value);
    setAddressError('');
    setPropertyAlreadyPostedWarningMessage('');
    setPartialAddress(e.target.value);
  };

  const handleSelectAddress = (option: ComboboxOptionType) => {
    const value = typeof option === 'string' ? option : option.value;
    setAddress(value);
    setAddressError('');
    void checkLocationRestriction(value, propertyType);
  };

  const handlePropertyTypeChange = (value: string) => {
    setPropertyType(value);
    setPropertyTypeError('');
    setUnit('');
    setNumUnits('');
    setNumUnitsError('');
    setUnitAlert(null);
    void checkLocationRestriction(address, value);
  };

  const handleUnitChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= AddressConstants.UNIT_NUMBER_LENGTH_LIMIT) {
      setUnit(e.target.value);
    }
  };

  const handleNumUnitsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNumUnits(value);
    if (value) {
      const error = validateNumUnits(value, propertyType);
      setNumUnitsError(error ?? '');
    } else {
      setNumUnitsError('');
    }
  };

  const advanceToReview = (data: RentalManagerPropertyAddressDetailsDto) => {
    onContinue({
      address: {
        street: data.street,
        city: data.city,
        state: data.state,
        zip: data.zip,
        unit: data.unitWithoutLabel || unit,
      },
      propertyType,
      numUnits,
      image: imageValue.image,
      imagePreviewUrl: imageValue.imagePreviewUrl,
      isImpreciseLocation: data.isInaccurateGeoPrecision ?? false,
      listingAlias: data.propertyId != null ? String(data.propertyId) : '',
    });
  };

  const handleContinue = async () => {
    let hasError = false;
    if (!address.trim()) {
      setAddressError('Street address is required');
      hasError = true;
    }
    if (!propertyType) {
      setPropertyTypeError('Property type is required');
      hasError = true;
    }
    const numUnitsValidationError = validateNumUnits(numUnits, propertyType);
    if (numUnitsValidationError) {
      setNumUnitsError(numUnitsValidationError);
      hasError = true;
    }
    if (hasError) return;

    // User already saw a unit alert for this same address+unit — acknowledge and proceed
    if (
      unitAlert !== null &&
      prevVerifiedRef.current.address === address &&
      prevVerifiedRef.current.unit === unit &&
      lastVerifiedDataRef.current
    ) {
      setUnitAlert(null);
      advanceToReview(lastVerifiedDataRef.current);
      return;
    }

    const { data, error } = await verifyAddress();
    if (error || !data) {
      setAddressError('Unable to verify this address. Check the address and try again.');
      return;
    }

    prevVerifiedRef.current = { address, unit };
    lastVerifiedDataRef.current = data;

    if (data.unitWithoutLabel) {
      setUnit(data.unitWithoutLabel);
    }

    const isMultiUnitType = propertyType === COMMUNITY || propertyType === MULTIPLEX;
    const alert = isMultiUnitType ? null : resolveUnitAlert(data, unit);
    if (alert !== null) {
      setUnitAlert(alert);
      return;
    }

    setUnitAlert(null);
    advanceToReview(data);
  };

  return {
    address,
    propertyType,
    unit,
    numUnits,
    imageValue,
    addressError,
    propertyTypeError,
    numUnitsError,
    propertyAlreadyPostedWarningMessage,
    unitAlert,
    isVerifyingAddress,
    isPropertyTypeDisabled: (isLoadingAddressDetails && !propertyType) || isCheckingRestrictions,
    addressInputRef,
    addressDetails: addressDetailsData,
    queryData,
    handleAddressChange,
    handleSelectAddress,
    handlePropertyTypeChange,
    handleUnitChange,
    handleNumUnitsChange,
    handleContinue,
    setImageValue,
  };
};
